import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CancelIcon from '@mui/icons-material/Cancel';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TableViewIcon from '@mui/icons-material/TableView';
import { useWebSocket } from '../WebSocketContext';
import { useSnackbar } from '../../Utils/SnackBarContext';
import { FixedSizeList } from 'react-window';
import JsonTableRenderer from '../Common/JsonTableRenderer';
import CloseButton from '../Common/CloseButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { updateTimeeventTag } from '../../Utils/timeEventsApi';

// Enhanced theme colors - matching the application design
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';
const gradientPrimary = 'linear-gradient(135deg, #5757b0, #4545a0)';
const shadowPrimary = '0 4px 20px rgba(87, 87, 176, 0.15)';

// Utility function for deep comparison of objects
const isEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj1 === obj2;
  }
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => isEqual(obj1[key], obj2[key]));
};

// Virtualized row component for large arrays - prevent focus loss
const VirtualizedTableBody = React.memo(({ data, children, maxHeight = 300, rowHeight = 53 }) => {
  // Only apply virtualization for larger datasets
  if (data.length <= 50) {
    return <TableBody>{children}</TableBody>;
  }

  return (
    <FixedSizeList
      height={Math.min(rowHeight * data.length, maxHeight)}
      width="100%"
      itemSize={rowHeight}
      itemCount={data.length}
      overscanCount={5}
    >
      {({ index, style }) => (
        <div style={style}>
          {children[index]}
        </div>
      )}
    </FixedSizeList>
  );
}, () => true); // Prevent re-renders completely during editing

// Custom component to render JSON data as a table with enhanced UI
const JsonTable = ({ data, isEditable = false, onDataChange = null }) => {
  // Local state for edit mode to handle immediate input changes
  const [localData, setLocalData] = useState(data);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize local data when entering edit mode for the first time
  React.useEffect(() => {
    if (isEditable && !isInitialized) {
      setLocalData(JSON.parse(JSON.stringify(data))); // Deep copy to avoid reference issues
      setIsInitialized(true);
    } else if (!isEditable) {
      setIsInitialized(false);
      setLocalData(data);
    }
  }, [isEditable, data, isInitialized]);
  
  // Handle local data changes and propagate to parent
  const handleLocalDataChange = useCallback((newData) => {
    setLocalData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }
  }, [onDataChange]);
  
  // Use local data for editing, external data for viewing
  const displayData = isEditable ? localData : data;

  // Handle different types of data
  if (typeof displayData !== 'object' || displayData === null) {
    return (
      <Box sx={{ p: 2 }}>
        {isEditable ? (
          <TextField 
            value={String(displayData)} 
            onChange={(e) => handleLocalDataChange(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: hoverColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: primaryColor,
                },
              },
            }}
          />
        ) : (
          <Typography 
            sx={{ 
              p: 2, 
              backgroundColor: lightPurple,
              borderRadius: '8px',
              border: `1px solid ${primaryColor}30`,
              fontFamily: 'monospace',
              fontSize: '0.9rem'
            }}
          >
            {String(displayData)}
          </Typography>
        )}
      </Box>
    );
  }

  // For arrays - using stable, non-changing keys
  if (Array.isArray(displayData)) {
    // Create table rows ahead of time for virtualization with stable keys
    const tableRows = displayData.map((item, index) => {
      // Use only index for key - don't include content that changes
      const rowKey = `array-row-${index}`;
      
      if (displayData.length > 0 && typeof displayData[0] === 'object' && displayData[0] !== null) {
        return (
          <TableRow 
            key={rowKey} 
            hover 
            sx={{
              '&:hover': {
                backgroundColor: `${lightPurple}80`,
                transition: 'background-color 0.2s ease',
              },
              '&:nth-of-type(even)': {
                backgroundColor: `${lightPurple}30`,
              },
            }}
          >
            {Object.entries(item)
              .map(([key, value], valueIndex) => {
                // Use stable key that doesn't change with content
                const cellKey = `${index}-${key}-${valueIndex}`;
                return (
                  <TableCell 
                    key={cellKey}
                    sx={{
                      padding: '12px 16px',
                      borderBottom: `1px solid ${primaryColor}20`,
                      fontSize: '0.875rem',
                    }}
                  >
                    {isEditable ? (
                      typeof value !== 'object' || value === null ? (
                        <TextField
                          value={String(value)}
                          onChange={(e) => {
                            const updatedData = [...displayData];
                            updatedData[index] = { ...item, [key]: e.target.value };
                            handleLocalDataChange(updatedData);
                          }}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              '&:hover fieldset': {
                                borderColor: hoverColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      ) : (
                        <Tooltip 
                          title="Complex objects can't be edited directly" 
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={Array.isArray(value) ? 'Array' : 'Object'}
                            size="small"
                            sx={{
                              backgroundColor: `${primaryColor}20`,
                              color: primaryColor,
                              cursor: 'not-allowed',
                              '&:hover': {
                                backgroundColor: `${primaryColor}30`,
                              },
                            }}
                          />
                        </Tooltip>
                      )
                    ) : (
                      typeof value === 'object' && value !== null ? 
                        <Tooltip 
                          title={<JsonTable data={value} />}
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={Array.isArray(value) ? 'Array' : 'Object'}
                            size="small"
                            clickable
                            icon={<DataObjectIcon />}
                            sx={{
                              backgroundColor: `${primaryColor}15`,
                              color: primaryColor,
                              cursor: 'help',
                              '&:hover': {
                                backgroundColor: `${primaryColor}25`,
                                transform: 'scale(1.02)',
                                transition: 'all 0.2s ease',
                              },
                            }}
                          />
                        </Tooltip> : 
                        <Typography 
                          component="span" 
                          sx={{ 
                            fontFamily: value !== null && typeof value === 'string' ? 'inherit' : 'monospace',
                            fontSize: '0.875rem',
                            color: value === null ? '#999' : 'inherit',
                            fontStyle: value === null ? 'italic' : 'normal',
                          }}
                        >
                          {value === null ? 'null' : String(value)}
                        </Typography>
                    )}
                  </TableCell>
                );
              })}
          </TableRow>
        );
      } else {
        return (
          <TableRow 
            key={rowKey} 
            hover
            sx={{
              '&:hover': {
                backgroundColor: `${lightPurple}80`,
                transition: 'background-color 0.2s ease',
              },
              '&:nth-of-type(even)': {
                backgroundColor: `${lightPurple}30`,
              },
            }}
          >
            <TableCell sx={{ padding: '12px 16px', borderBottom: `1px solid ${primaryColor}20` }}>
              {isEditable ? (
                typeof item !== 'object' || item === null ? (
                  <TextField
                    value={String(item)}
                    onChange={(e) => {
                      const updatedData = [...displayData];
                      updatedData[index] = e.target.value;
                      handleLocalDataChange(updatedData);
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&:hover fieldset': {
                          borderColor: hoverColor,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: primaryColor,
                        },
                      },
                    }}
                  />
                ) : (
                  <JsonTable 
                    data={item} 
                    isEditable={isEditable}
                    onDataChange={(newValue) => {
                      const updatedData = [...displayData];
                      updatedData[index] = newValue;
                      handleLocalDataChange(updatedData);
                    }}
                  />
                )
              ) : (
                typeof item === 'object' && item !== null ? 
                  <JsonTable data={item} /> : 
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: item !== null && typeof item === 'string' ? 'inherit' : 'monospace',
                      fontSize: '0.875rem',
                      color: item === null ? '#999' : 'inherit',
                      fontStyle: item === null ? 'italic' : 'normal',
                    }}
                  >
                    {item === null ? 'null' : String(item)}
                  </Typography>
              )}
            </TableCell>
          </TableRow>
        );
      }
    });

    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: '65vh',
          borderRadius: '12px',
          boxShadow: shadowPrimary,
          border: `1px solid ${primaryColor}20`,
          overflow: 'auto',
          width: '100%'
        }}
      >
        <Table size="small" stickyHeader>
          {displayData.length > 0 && typeof displayData[0] === 'object' && displayData[0] !== null && (
            <TableHead>
              <TableRow>
                {Object.keys(displayData[0]).map((key) => (
                  <TableCell 
                    key={key} 
                    sx={{ 
                      fontWeight: 'bold', 
                      background: gradientPrimary,
                      color: 'white',
                      padding: '16px',
                      fontSize: '0.9rem',
                      textTransform: 'capitalize',
                      borderBottom: 'none',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TableViewIcon fontSize="small" />
                      {key}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          {/* Disable virtualization in edit mode to prevent focus loss */}
          {isEditable ? (
            <TableBody>
              {tableRows}
            </TableBody>
          ) : (
            <VirtualizedTableBody data={displayData}>
              {tableRows}
            </VirtualizedTableBody>
          )}
        </Table>
      </TableContainer>
    );
  }

  // For objects - using stable keys that don't change with content
  const entries = Object.entries(displayData);

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        maxHeight: '65vh',
        borderRadius: '12px',
        boxShadow: shadowPrimary,
        border: `1px solid ${primaryColor}20`,
        overflow: 'auto',
        width: '100%'
      }}
    >
      <Table size="small">
        <TableBody>
          {entries.map(([key, value], rowIndex) => {
            // Use only the original key and index - don't include changing content
            const rowKey = `object-row-${key}-${rowIndex}`;
            return (
              <TableRow 
                key={rowKey} 
                hover
                sx={{
                  '&:hover': {
                    backgroundColor: `${lightPurple}80`,
                    transition: 'background-color 0.2s ease',
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: `${lightPurple}30`,
                  },
                }}
              >
                <TableCell 
                  component="th" 
                  scope="row" 
                  sx={{ 
                    fontWeight: 'bold', 
                    background: `${primaryColor}15`,
                    width: '30%',
                    padding: '16px',
                    borderBottom: `1px solid ${primaryColor}20`,
                    borderRight: `1px solid ${primaryColor}20`,
                    fontSize: '0.875rem',
                    color: primaryColor,
                  }}
                >
                  {isEditable ? (
                    <TextField
                      value={key}
                      onChange={(e) => {
                        const newKey = e.target.value;
                        const updatedData = { ...displayData };
                        
                        // Skip if key already exists
                        if (newKey !== key && updatedData.hasOwnProperty(newKey)) {
                          return;
                        }
                        
                        // Create a new object with the updated key
                        delete updatedData[key];
                        updatedData[newKey] = value;
                        
                        handleLocalDataChange(updatedData);
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          '&:hover fieldset': {
                            borderColor: hoverColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DataObjectIcon fontSize="small" />
                      {key}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ padding: '16px', borderBottom: `1px solid ${primaryColor}20` }}>
                  {isEditable ? (
                    typeof value !== 'object' || value === null ? (
                      <TextField
                        value={String(value)}
                        onChange={(e) => {
                          handleLocalDataChange({ ...displayData, [key]: e.target.value });
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            '&:hover fieldset': {
                              borderColor: hoverColor,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: primaryColor,
                            },
                          },
                        }}
                      />
                    ) : (
                      <JsonTable 
                        data={value} 
                        isEditable={isEditable}
                        onDataChange={(newValue) => {
                          handleLocalDataChange({ ...displayData, [key]: newValue });
                        }}
                      />
                    )
                  ) : (
                    typeof value === 'object' && value !== null ? 
                      <JsonTable data={value} /> : 
                      <Typography 
                        component="span" 
                        sx={{ 
                          fontFamily: value !== null && typeof value === 'string' ? 'inherit' : 'monospace',
                          fontSize: '0.875rem',
                          color: value === null ? '#999' : 'inherit',
                          fontStyle: value === null ? 'italic' : 'normal',
                        }}
                      >
                        {value === null ? 'null' : String(value)}
                      </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TimeEventJsonViewer = ({
  open, 
  onClose, 
  title, 
  data, 
  fieldId,
  onSave,
  caseid,
  tag = null // Add tag prop to access full tag information for server saves
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const { openSnackbar } = useSnackbar();

  // Responsive: use full screen dialog on small viewports
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle WebSocket messages for export responses
  useEffect(() => {
    const watchedCommands = ['exportexcel_from_variable'];
    const handleExportResponse = (parsedMessage = {}) => {
      const { command, data = {} } = parsedMessage;
      if (command !== 'exportexcel_from_variable') {
        return;
      }

      setIsLoading(false);
      if (data.status === 'success' && data.data) {
        // Handle successful export with base64 data
        try {
          // Prevent duplicate downloads by checking if this file was already downloaded
            const downloadId = `${data.filename}_${data.count}`;
            const downloadKey = `excel_download_${downloadId}`;
            
            // Check if we already downloaded this file in the last 5 seconds
            const lastDownload = sessionStorage.getItem(downloadKey);
            const now = Date.now();
            if (lastDownload && (now - parseInt(lastDownload)) < 5000) {
              console.log('Preventing duplicate download for:', downloadId);
              return;
            }
            
            // Mark this download as processed
            sessionStorage.setItem(downloadKey, now.toString());
            
            // Convert base64 to blob
            const binaryString = atob(data.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            // Create download link and trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = data.filename || 'export.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            openSnackbar(`Excel export successful (${data.count || 0} records)`, 3000, 'success');
          } catch (error) {
            console.error('Error processing Excel download:', error);
            openSnackbar('Failed to download the Excel file', 3000, 'error');
          }
      } else if (data.status === 'error') {
        openSnackbar(data.message || 'Export failed', 3000, 'error');
      } else if (data.status === 'warning') {
        openSnackbar(data.message || 'Export completed with warnings', 4000, 'warning');
      }
    };

    return subscribeToMessages(watchedCommands, handleExportResponse);
  }, [subscribeToMessages, openSnackbar]);

  // Memoize the data deep copy operation when switching to edit mode
  const handleEditJson = useCallback(() => {
    setIsEditing(true);
    setEditableData(JSON.parse(JSON.stringify(data)));
  }, [data]);
  
  // Handle JSON edit save in dialog
  const handleSaveJson = useCallback(async () => {
    try {
      // Tag changes use the authenticated HTTP API; websocket remains only for exports.
      if (tag) {
        const updatedReply = JSON.stringify(editableData);
        
        // Only send update if the value actually changed
        if (updatedReply !== String(tag.reply || '')) {
          // Create updated field object like TimeEventTag does
          const updatedField = {
            ...tag,
            reply: updatedReply,
            needs_review: false,
            review_reason: '',
            confidence: null,
            confidence_source: 'manual'
          };
          
          await updateTimeeventTag(tag.timeeventid, updatedField);
          
          openSnackbar("Data saved successfully", 2000, "success");
          
          // Call onSave if provided for additional handling
          if (onSave) {
            onSave(editableData);
          }
        } else {
          openSnackbar("No changes to save", 1000, "info");
        }
      } else if (onSave) {
        // Fallback to parent handling if no tag is available
        onSave(editableData);
        openSnackbar("Data saved successfully", 2000, "success");
      } else {
        openSnackbar("Cannot save: missing tag information", 3000, "error");
        return;
      }
      
      // Exit edit mode
      setIsEditing(false);
    } catch (error) {
      openSnackbar("Invalid JSON format. Please check your input.", 3000, "error");
    }
  }, [editableData, onSave, openSnackbar, tag]);

  // Handle cancel editing
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditableData(null);
  }, []);

  // Handle editable JSON data change - using useCallback to prevent unnecessary re-renders
  const handleEditableJsonChange = React.useCallback((newData) => {
    setEditableData(newData);
  }, []);

  // Handle exporting the variable to Excel
  const handleExportToExcel = () => {
    if (caseid) {
      setIsLoading(true);
      sendMessage({
        command: 'exportexcel_from_variable',
        caseid: caseid,
        variable: title
      });
      openSnackbar(`Exporting data for ${title} to Excel...`, 2000);
    } else {
      openSnackbar('Cannot export data: case ID not available', 3000, 'error');
    }
  };

  // Enhanced close handler
  const handleClose = useCallback(() => {
    onClose();
    setIsEditing(false);
    setEditableData(null);
    setIsLoading(false);
  }, [onClose]);

  // Memoized dialog content to prevent unnecessary re-renders
  const dialogContent = useMemo(() => (
    <DialogContent sx={{ 
      "--dialog-content-top-padding": "0px",
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 3,
      paddingRight: 3,
      minHeight: '300px',
      backgroundColor: '#fafafa',
    }}>
      <Box sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
        {/* Main Content */}
        {!isEditing ? (
          <JsonTableRenderer 
            data={data} 
            maxHeight="65vh" 
            maxItems={2000} 
            showIndex={true} 
            compact={false} 
          />
        ) : (
          <Box sx={{ width: '100%' }}>
            <JsonTable 
              data={editableData} 
              isEditable={true}
              onDataChange={handleEditableJsonChange}
            />
            <Card sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: '#fff3e0',
              border: '1px solid #ffb74d',
              borderRadius: '8px',
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: '0.875rem',
              }}>
                <EditIcon fontSize="small" />
                Edit both keys and values directly in the table. Click Save to apply changes or Cancel to discard.
              </Typography>
            </Card>
          </Box>
        )}
      </Box>
    </DialogContent>
  ), [isEditing, data, editableData, handleEditableJsonChange]);

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmallScreen}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(87, 87, 176, 0.2)',
            background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: gradientPrimary,
          color: 'white',
          fontWeight: 'bold',
          borderBottom: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          padding: { xs: '12px 16px', sm: '20px 24px' },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 20% 25%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 40%)',
            opacity: 0.8,
            zIndex: 0,
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1, flex: '1 1 auto', minWidth: 0 }}>
            <DataObjectIcon sx={{ fontSize: '1.5rem' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                {isEditing ? "Edit Mode - Make your changes below" : "View Mode - Data is read-only"}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1, flexShrink: 0, flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: 'flex-end' }}>
            {!isEditing && (
              <Tooltip title={`Export ${title} data to Excel`} arrow>
                <Button 
                  onClick={handleExportToExcel}
                  disabled={isLoading}
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    },
                    borderRadius: '8px',
                    padding: { xs: '6px 8px', sm: '8px 16px' },
                    fontWeight: 'medium',
                    textTransform: 'none',
                    mr: 1,
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
                  }}
                  startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{isLoading ? 'Exporting...' : 'Export to Excel'}</Box>
                </Button>
              </Tooltip>
            )}
            
            {isEditing ? (
              <>
                <Button 
                  onClick={handleSaveJson}
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(76, 175, 80, 0.9)',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 1)',
                    },
                    borderRadius: '8px',
                    padding: { xs: '6px 8px', sm: '8px 16px' },
                    fontWeight: 'medium',
                    textTransform: 'none',
                    mr: 1,
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
                  }}
                  startIcon={<SaveIcon />}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Save Changes</Box>
                </Button>
                <Button 
                  onClick={handleCancelEdit}
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(244, 67, 54, 0.9)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 1)',
                    },
                    borderRadius: '8px',
                    padding: { xs: '6px 8px', sm: '8px 16px' },
                    fontWeight: 'medium',
                    textTransform: 'none',
                    mr: 1,
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
                  }}
                  startIcon={<CancelIcon />}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Cancel</Box>
                </Button>
              </>
            ) : (
              <Button 
                onClick={handleEditJson}
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  },
                  borderRadius: '8px',
                  padding: { xs: '6px 8px', sm: '8px 16px' },
                  fontWeight: 'medium',
                  textTransform: 'none',
                  mr: 1,
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
                }}
                startIcon={<EditIcon />}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Edit Data</Box>
              </Button>
            )}
            
            <CloseButton
              ariaLabel="close"
              onClick={handleClose}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
                borderRadius: '8px',
                ml: { xs: 0.5, sm: 0 }
              }}
            />
          </Box>
        </DialogTitle>
        {dialogContent}
      </Dialog>
    </>
  );
};

export default React.memo(TimeEventJsonViewer, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  if (prevProps.open !== nextProps.open) return false;
  if (prevProps.title !== nextProps.title) return false;
  if (prevProps.fieldId !== nextProps.fieldId) return false;
  if (prevProps.caseid !== nextProps.caseid) return false;
  
  // Deep comparison for data
  return isEqual(prevProps.data, nextProps.data);
});

export { JsonTable };
